import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { Events } from '@df/core/service/event';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsTrayService } from '@icms/ui/tray/icms-tray.service';
import { config } from '../../../catalog/ui/page/component/submenu/submenu.config';
import { NCustomerGroups } from '../../../customer/groups/customer-groups';
import { CustomerGroupsService } from '../../../customer/groups/customer-groups.service';
import { NPageHeader } from '../../page/header/page-header.interface';
import { PageHeaderMaganavContentAreaDirective } from './area/page-header-meganav-content-area.directive';

/**
 * Meganav content.
 *
 * @usage
 * ```html
 * <meganav-content></meganav-content>
 * ```
 */
@Component({
    selector: 'meganav-content',
    templateUrl: 'meganav-content.component.html',
    styleUrl: './meganav-content.component.scss',
    standalone: true,
    imports: [CommonModule, ElementHeightDirective, ICmsClientModule, PageHeaderMaganavContentAreaDirective, ToggleModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MeganavContentComponent extends UiComponent {
    @Input()
    items!: NPageHeader.IMenuItem[];

    @Input()
    content!: NPageHeader.IBlockDataContent;

    /**
     * Allowed component ids
     */
    allowOnly = [config.id];

    readonly toggleService = inject(ToggleService);
    readonly events = inject(Events);
    readonly customerGroupsService = inject(CustomerGroupsService);
    protected iCmsTrayService = inject(ICmsTrayService);

    /**
     * Group id for toggle service
     */
    readonly toggleGroupId = NPageHeader.MEGANAV_GROUP_ID;

    /**
     * Group id for toggle service
     */
    readonly searchMeganavKey = 'search';

    constructor() {
        super();

        this.subscriptions.add(
            this.client.isCms$.subscribe(open => {
                if (!open) {
                    this.toggleService.closeGroup(this.toggleGroupId);
                }
            })
        );
    }

    get isCms(): boolean {
        return this.client.isCms && !this.client.isCmsPreview;
    }

    handleMouseEnter(index: number): void {
        this.toggleService.open([this.toggleGroupId, index]);
    }

    handleMouseLeave(index: number): void {
        if (!this.isCms) {
            this.toggleService.close([this.toggleGroupId, index], { delay: 300 });
        }
    }

    /**
     * Checks if menu item can be printed (has items or cms)
     */
    canPrintContent(item: NPageHeader.IMenuItem): boolean {
        return !!(this.client.isCms || item.components?.length);
    }

    ifCustomerGroup(value: NCustomerGroups.TId | NCustomerGroups.TId[]): boolean {
        return this.customerGroupsService.isAllowed(value);
    }
}
